/** LeaderboardPanel — schools leaderboard + point activities + journey shelves. Extracted from panels.jsx. */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Star, TrendingUp } from "lucide-react";
import { BadgeShelf } from "@/components/BadgeShelf";
import { PointsLedger } from "@/components/PointsLedger";

export function LeaderboardPanel({ leaderboard, activities, reload }) {
  const [awardOpen, setAwardOpen] = useState(false);
  const [award, setAward] = useState({ school_id: leaderboard[0]?.id || "", delta: 300, reason: "" });
  useEffect(() => { if (!award.school_id && leaderboard[0]) setAward((a) => ({ ...a, school_id: leaderboard[0].id })); }, [leaderboard]);
  const submit = async () => {
    try {
      await http.post("/leaderboard/award", award);
      toast.success("Points awarded"); setAwardOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Schools leaderboard</CardTitle>
          <Button size="sm" data-testid="award-points-btn" onClick={() => setAwardOpen(true)}><Star className="w-4 h-4 mr-1" /> Award points</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {leaderboard.map((s, i) => {
            const g = s.gamification;
            const pct = g?.next ? Math.min(100, Math.round(((g.points - g.current.min) / (g.next.min - g.current.min)) * 100)) : 100;
            return (
              <div key={s.id} data-testid={`leader-row-${s.id}`} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full grid place-items-center font-bold text-white" style={{ background: g?.current?.color || "#94A3B8" }}>{i + 1}</div>
                    <div>
                      <div className="font-heading font-semibold text-slate-900">{s.name}</div>
                      <div className="text-xs text-slate-500">{s.city} · {s.school_code}.{s.location_code}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Lv {g?.current?.level}</div>
                    <div className="font-heading font-bold text-slate-900">{g?.current?.name}</div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, background: g?.current?.color }} />
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                    <span className="font-semibold text-slate-800">{(s.points || 0).toLocaleString()} points</span>
                    <span>{g?.next ? `${g.points_to_next.toLocaleString()} to ${g.next.name}` : "Max level"}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Point activities</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {activities.map((a) => (
            <div key={a.activity} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2">
              <div className="text-sm text-slate-700">{a.activity}</div>
              <Badge className="bg-sky-500">+{a.points}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={awardOpen} onOpenChange={setAwardOpen}>
        <DialogContent data-testid="award-dialog">
          <DialogHeader><DialogTitle>Award school points</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>School</Label>
              <select data-testid="award-school-select" value={award.school_id} onChange={(e) => setAward({ ...award, school_id: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 bg-white text-sm">
                {leaderboard.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div><Label>Points</Label><Input data-testid="award-points-input" type="number" value={award.delta} onChange={(e) => setAward({ ...award, delta: Number(e.target.value) })} className="mt-1" /></div>
            <div><Label>Reason</Label><Input data-testid="award-reason-input" value={award.reason} onChange={(e) => setAward({ ...award, reason: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="award-submit-btn" onClick={submit}>Award</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Journey map — 8-level KOI progression per school */}
      <Card className="lg:col-span-3">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base">School journey · 8 KOI levels</CardTitle>
          <div className="text-xs text-slate-500 mt-1">Each school&rsquo;s progression through the KOI wellness levels.</div>
        </CardHeader>
        <CardContent className="space-y-6">
          {leaderboard.map((s) => (
            <div key={s.id} data-testid={`journey-${s.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-slate-800">{s.name}</div>
                <Badge variant="outline">{(s.points || 0).toLocaleString()} pts</Badge>
              </div>
              <BadgeShelf points={s.points || 0} testId={`journey-shelf-${s.id}`} />
              <div className="mt-4">
                <PointsLedger entityType="school" entityId={s.id} title={`What earned ${s.name}'s points`} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
